import React, { useEffect, useState, useTransition } from 'react';
import { getAuth } from 'firebase/auth';
import useFirebase from './index';
import { Spin } from 'antd';
import { LoadingWrapper } from '@ui/Utility/Loader';
import { UserPermissions, getUserPermissions } from '@shared/permissions';

interface AuthContextType {
  currentUser: any | null;
  userEmail: string | null;
  isLoading: boolean;
  /** Resolved permissions for the current user (in-code map). */
  userPermissions: UserPermissions | null;
}

export const AuthContext = React.createContext<AuthContextType>({
  currentUser: null,
  userEmail: null,
  isLoading: true,
  userPermissions: null,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isPending, startTransition] = useTransition();
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user?.email) {
        const userPerms = getUserPermissions(user.email);
        startTransition(() => {
          setCurrentUser(user);
          setUserEmail(user.email);
          setUserPermissions(userPerms);
          setIsLoading(false);
        });
      } else {
        startTransition(() => {
          setCurrentUser(user);
          setUserEmail(user?.email ?? null);
          setUserPermissions(null);
          setIsLoading(false);
        });
      }
    });

    return () => unsubscribe();
  }, []);

  if (isLoading || isPending) {
    return (
      <LoadingWrapper key="auth-loading">
        <Spin size="large" tip="Loading..." />
      </LoadingWrapper>
    );
  }

  return (
    <AuthContext.Provider value={{ currentUser, userEmail, isLoading, userPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};
